import type { HelmAccess } from '../roles'
import type { TourDefinition, TourPage, TourStep } from './tour-machine'

/**
 * The guided tours in Getting started. Each step points at an element marked
 * `data-tour="…"` on the real screen; `tests/helm_onboarding_tours.test.ts`
 * checks every name used here is still on a screen.
 */

const PAGES = {
  inquiries: { match: /^\/inquiries\/?$/, href: '/inquiries', label: 'Inquiries' },
  newInquiry: { match: /^\/inquiries\/new\/?$/, href: '/inquiries/new', label: 'New Inquiry' },
  inquiry: { match: /^\/inquiries\/(?!new\/?$)[^/]+\/?$/, label: "an inquiry's page" },
  newConsignment: {
    match: /^\/consignments\/new\/?$/,
    href: '/consignments/new',
    label: 'New Consignment',
  },
  consignment: {
    match: /^\/consignments\/(?!new\/?$)[^/]+(\/[^/]+)?\/?$/,
    label: "a consignment's page",
  },
  warehouse: { match: /^\/warehouse\/?$/, href: '/warehouse', label: 'Warehouse' },
} satisfies Record<string, TourPage>

/** A menu item: on a phone it is behind the menu button until the menu opens. */
const IN_MENU = { target: 'nav-menu', note: "It's in the menu: press the menu button to show it." }

const staff = (access: HelmAccess) => !access.customer
const office = (access: HelmAccess) => !access.customer && !access.warehouseOnly

function menuStep(step: Omit<TourStep, 'fallback' | 'side'>): TourStep {
  return { ...step, fallback: IN_MENU, side: 'right' }
}

const navigation: TourDefinition = {
  id: 'navigation',
  title: 'Find your way around',
  summary: 'Where everything is in Helm, and where to get help.',
  audience: () => true,
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to Helm',
      body: "Helm is where Xtellar runs each customer's request, from the first message to delivery. This tour shows where things are. Press Next to go on, or Esc to stop; you can restart it from Help.",
      when: staff,
    },
    {
      id: 'welcome-customer',
      title: 'Welcome to Helm',
      body: 'This short tour shows where things are. Press Next to go on, or Esc to stop; you can restart it from Help.',
      when: (access) => access.customer,
    },
    menuStep({
      id: 'groups',
      target: 'nav-group-shipments',
      title: 'A menu in groups',
      body: "The menu is grouped by kind of work. Press a group's heading to fold it away; Helm remembers, and always opens the group of the page you are on.",
      when: office,
    }),
    menuStep({
      id: 'dashboard',
      target: 'nav-dashboard',
      title: 'Dashboard',
      body: 'The overview: consignments, open inquiries, sea tracking and the taxes estimated so far, with the latest inquiries.',
      when: office,
    }),
    menuStep({
      id: 'attention',
      target: 'nav-attention',
      title: 'Attention',
      body: 'What the team owes clients: replies, quotations and tasks, each with the time left in Kampala working hours. Start the day here.',
      when: office,
    }),
    menuStep({
      id: 'inquiries',
      target: 'nav-inquiries',
      title: 'Inquiries',
      body: 'Every request (sourcing, shipping, PVoC testing, customs clearance) starts here, and is qualified and quoted before work begins.',
      when: office,
    }),
    menuStep({
      id: 'consignments',
      target: 'nav-consignments',
      title: 'Consignments',
      body: 'Goods that are on the move: items and HS codes, taxes, documents, tracking and updates to the customer.',
      when: office,
    }),
    menuStep({
      id: 'customers',
      target: 'nav-customers',
      title: 'Customers',
      body: "Each customer's details and contacts, with their consignments, invoices and activity.",
      when: office,
    }),
    menuStep({
      id: 'warehouse',
      target: 'nav-warehouse',
      title: 'Warehouse',
      body: 'The Guangzhou warehouse: parcels customers are sending, what has arrived, and the week each one flies.',
      when: (access) => access.seesWarehouse,
    }),
    menuStep({
      id: 'parcels',
      target: 'nav-parcels',
      title: 'My parcels',
      body: 'Tell the warehouse in Guangzhou a parcel is coming, and follow it until it flies.',
      when: (access) => access.customer,
    }),
    menuStep({
      id: 'create',
      target: 'nav-new',
      title: 'New',
      body: 'Start a new inquiry, consignment or customer from any page.',
      when: office,
    }),
    {
      id: 'beacon',
      target: 'beacon-launcher',
      title: 'Beacon',
      body: "Helm's assistant, on every page. Ask it to find a record, draft a reply or update something; it asks you before it changes anything, and keeps the conversation while you move around.",
      missing: 'Beacon is open full size on this page.',
      when: office,
    },
    {
      id: 'notifications',
      target: 'header-notifications',
      title: 'Notifications',
      body: 'Assignments, replies and anything else waiting on you. The number is how many you have not read.',
    },
    {
      id: 'help',
      target: 'header-help',
      title: 'Help',
      body: 'Getting started lives here: take another tour, or restart one. Fields with an info icon say what they are for.',
    },
    {
      id: 'report',
      target: 'header-report',
      title: 'Report a problem',
      body: 'Something wrong, or an idea? Tell engineering here. The page and recent errors are attached for you.',
      when: staff,
    },
  ],
}

const createInquiry: TourDefinition = {
  id: 'create-inquiry',
  title: 'Create an inquiry',
  summary: 'Capture a customer request: who it is for, what they want and how it travels.',
  audience: office,
  steps: [
    menuStep({
      id: 'open',
      target: 'nav-new',
      title: 'Start a new inquiry',
      body: 'Every request starts as an inquiry. Press New and choose Inquiry, or press Next.',
      advance: { page: PAGES.newInquiry },
      perform: { navigate: PAGES.newInquiry.href },
      lead: true,
    }),
    {
      id: 'steps',
      page: PAGES.newInquiry,
      target: 'wizard-steps',
      title: 'Five short steps',
      body: 'Source and Shipping are optional. Your answers are kept in this browser until the inquiry is created, so a refresh loses nothing.',
      side: 'right',
    },
    {
      id: 'source',
      page: PAGES.newInquiry,
      target: 'inquiry-source',
      title: 'Where it came from',
      body: "Drop, paste or choose a screenshot of the client's message, or paste a link, then press Fill in from the source: Helm fills in the next steps for you to check. Next moves the form on.",
      advance: { target: 'inquiry-client' },
      perform: { click: 'wizard-next' },
      missing: 'This is on the Source step of the form.',
    },
    {
      id: 'client',
      page: PAGES.newInquiry,
      target: 'inquiry-client',
      title: 'Who is asking',
      body: "Pick the existing customer. If you can't confirm who it is yet, set Customer identity to Identity not confirmed: Helm creates a provisional customer you can match later, and merges nothing on its own.",
      missing: 'This is on the Client & services step of the form.',
    },
    {
      id: 'services',
      page: PAGES.newInquiry,
      target: 'inquiry-services',
      title: 'What they need',
      body: 'Tick every service the client wants; it decides what the team does next. Reach them by is how updates go out. Next moves the form on once a client is chosen.',
      advance: { target: 'inquiry-products' },
      perform: { click: 'wizard-next' },
      missing: 'This is on the Client & services step of the form.',
    },
    {
      id: 'products',
      page: PAGES.newInquiry,
      target: 'inquiry-products',
      title: 'What they want',
      body: 'Name each product with its quantity and unit price. The HS code is the customs classification that sets the duty; leave it empty if you are unsure. The info icons say what each field is for.',
      advance: { target: 'inquiry-shipping' },
      perform: { click: 'wizard-next' },
      missing: 'This is on the Products step of the form.',
    },
    {
      id: 'shipping',
      page: PAGES.newInquiry,
      target: 'inquiry-shipping',
      title: 'How it travels',
      body: "Mode, origin and destination, and the Incoterm: where the supplier's job ends and the buyer's begins (FOB, CIF, DDP…). Fill in what is known; the rest can follow.",
      advance: { target: 'inquiry-follow-up' },
      perform: { click: 'wizard-next' },
      missing: 'This is on the Shipping & supplier step of the form.',
    },
    {
      id: 'follow-up',
      page: PAGES.newInquiry,
      target: 'inquiry-follow-up',
      title: 'Who does what next',
      body: "Submitted puts it in the team's queue; Draft keeps it internal. Next action and Reply due by put it on the Attention page, so the client hears back on time.",
      missing: 'This is on the Follow-up & review step of the form.',
    },
    {
      id: 'create',
      page: PAGES.newInquiry,
      target: 'wizard-submit',
      title: 'Create it',
      body: 'Check the summary, then press Create inquiry yourself. Helm opens the new inquiry; Turn an inquiry into a quote carries on from there.',
      missing: 'This is at the end of the form.',
    },
  ],
}

const quote: TourDefinition = {
  id: 'quote',
  title: 'Turn an inquiry into a quote',
  summary: 'Collect supplier quotes, record the one the client accepts, send the quote, convert.',
  // Supplier options, the workflow and converting all need admin access.
  audience: (access) => office(access) && access.admin,
  steps: [
    menuStep({
      id: 'find',
      target: 'nav-inquiries',
      title: 'Find the inquiry',
      body: 'A quote is built on the inquiry itself. Open Inquiries, or press Next.',
      advance: { page: PAGES.inquiries },
      perform: { navigate: PAGES.inquiries.href },
      lead: true,
    }),
    {
      id: 'pick',
      page: PAGES.inquiries,
      target: 'inquiry-list',
      title: 'Open the inquiry',
      body: 'Open the inquiry you are quoting from the list. Take it if nobody owns it yet.',
      advance: { page: PAGES.inquiry },
      lead: true,
    },
    {
      id: 'items',
      page: PAGES.inquiry,
      target: 'inquiry-items',
      title: 'Check what is asked',
      body: 'The requested items are what you quote: confirm names, quantities and target prices with the client, and add HS codes where you know them.',
    },
    {
      id: 'suppliers',
      page: PAGES.inquiry,
      target: 'inquiry-suppliers',
      title: 'Collect supplier quotes',
      body: 'Add Option for each factory you ask. Move its quote status along as replies come in (requested, received, proforma uploaded) and attach the quotation.',
    },
    {
      id: 'confirm',
      page: PAGES.inquiry,
      target: 'inquiry-suppliers',
      title: 'Record what the client accepts',
      body: 'Press Confirm on the option the client accepts. It becomes the customer-approved supplier, and Helm moves the inquiry on towards Quoted.',
    },
    {
      id: 'workflow',
      page: PAGES.inquiry,
      target: 'inquiry-workflow',
      title: 'Keep the workflow current',
      body: 'Phase shows where the inquiry is. Set the next task and when it is due, then Save: a dated task shows on the Attention page.',
    },
    {
      id: 'send',
      page: PAGES.inquiry,
      target: 'inquiry-communication',
      title: 'Send the quote',
      body: 'Choose the Quote sent template, attach the quotation, and send it by email or WhatsApp. Every update is kept in the timeline.',
    },
    {
      id: 'convert',
      page: PAGES.inquiry,
      target: 'inquiry-convert',
      title: 'When the client says yes',
      body: 'Convert to consignment opens the operational consignment with these items once work starts. Add the tracking reference if you already have it.',
    },
  ],
}

const consignment: TourDefinition = {
  id: 'consignment',
  title: 'Create and update a consignment',
  summary: 'Open a consignment, classify its items, estimate the taxes and keep it up to date.',
  audience: office,
  steps: [
    menuStep({
      id: 'open',
      target: 'nav-new',
      title: 'Open a consignment',
      body: 'A consignment is goods on the move. Press New and choose Consignment, or press Next. Converting an inquiry opens one too.',
      advance: { page: PAGES.newConsignment },
      perform: { navigate: PAGES.newConsignment.href },
      lead: true,
    }),
    {
      id: 'customer',
      page: PAGES.newConsignment,
      target: 'consignment-customer',
      title: 'Who it is for',
      body: 'Choose the customer and the services. Stage is where the deal stands commercially, separate from where the goods are. Next moves the form on.',
      advance: { target: 'consignment-shipping' },
      perform: { click: 'wizard-next' },
      missing: 'This is on the Customer & service step of the form.',
    },
    {
      id: 'shipping',
      page: PAGES.newConsignment,
      target: 'consignment-shipping',
      title: 'How it travels',
      body: 'Mode and destination are needed. Add the tracking number when you have it: the B/L for sea, the air waybill for air, or a container number.',
      advance: { target: 'consignment-invoice' },
      perform: { click: 'wizard-next' },
      missing: 'This is on the Shipping step of the form.',
    },
    {
      id: 'invoice',
      page: PAGES.newConsignment,
      target: 'consignment-invoice',
      title: 'Let Helm read the invoice',
      body: "Upload the supplier's invoice, a PDF or a photo, and Helm lists its items for you to check. No invoice yet? Go on and add them by hand.",
      advance: { target: 'consignment-items' },
      perform: { click: 'wizard-next' },
      missing: 'This is on the Invoice step of the form.',
    },
    {
      id: 'items',
      page: PAGES.newConsignment,
      target: 'consignment-items',
      title: 'Items and HS codes',
      body: 'Each item needs a name, quantity and unit price. Classify suggests the HS code from the description and photo; check it, because the code sets the duty rate.',
      advance: { target: 'consignment-tax' },
      perform: { click: 'wizard-next' },
      missing: 'This is on the Items & HS codes step of the form.',
    },
    {
      id: 'tax',
      page: PAGES.newConsignment,
      target: 'consignment-tax',
      title: 'Estimate the taxes',
      body: 'Calculate tax works out import duty, excise, VAT, withholding tax and the infrastructure levy on the CIF value, in Uganda shillings. It is saved with the consignment.',
      missing: 'This is on the Review & tax step of the form.',
    },
    {
      id: 'create',
      page: PAGES.newConsignment,
      target: 'wizard-submit',
      title: 'Create it',
      body: 'Press Create consignment yourself when it is right. Helm opens it so you can keep it up to date.',
      missing: 'This is at the end of the form.',
    },
    {
      id: 'edit',
      page: PAGES.consignment,
      target: 'consignment-edit',
      title: 'Update it as it moves',
      body: 'Edit changes the phase, state, owner, next task, tracking number, status and payment status. Add an update comment so the history says why.',
      when: (access) => access.admin,
    },
    {
      id: 'tabs',
      page: PAGES.consignment,
      target: 'consignment-tabs',
      title: 'Everything else, by tab',
      body: 'Items and HS codes, the tax breakdown, documents to generate or upload, tracking, and messages to the customer each have a tab.',
    },
  ],
}

const receiveParcel: TourDefinition = {
  id: 'receive-parcel',
  title: 'Receive a parcel',
  summary:
    'Find a parcel by its label, photograph, weigh and measure it, and book its flight week.',
  audience: (access) => access.seesWarehouse,
  steps: [
    menuStep({
      id: 'open',
      target: 'nav-warehouse',
      title: 'Open the warehouse',
      body: 'Parcels are received on the Warehouse page. Open it, or press Next.',
      advance: { page: PAGES.warehouse },
      perform: { navigate: PAGES.warehouse.href },
      lead: true,
    }),
    {
      id: 'find',
      page: PAGES.warehouse,
      target: 'warehouse-receive',
      title: 'Find or receive it',
      body: 'Scan or type the tracking number on the label, or the customer code (XTL…), and press Find or receive. A parcel nobody announced is registered so it can still be processed.',
      advance: { target: 'warehouse-parcel' },
    },
    {
      id: 'photos',
      page: PAGES.warehouse,
      target: 'warehouse-photos',
      title: 'Photograph it',
      body: 'Take at least one photo. Helm will not mark a parcel processed without one.',
      missing: 'Open a parcel first: find one above, or pick one from the list.',
    },
    {
      id: 'measure',
      page: PAGES.warehouse,
      target: 'warehouse-measure',
      title: 'Weigh and measure it',
      body: 'Enter the weight. Add length, width and height and Helm charges the greater of the actual weight and the volume weight (L × W × H ÷ 6000): the chargeable weight.',
      missing: 'Open a parcel first: find one above, or pick one from the list.',
    },
    {
      id: 'dangerous',
      page: PAGES.warehouse,
      target: 'warehouse-dangerous',
      title: 'Dangerous goods',
      body: 'Tick it for batteries, liquids, powders, magnets, perfume or anything flammable. It is charged at the dangerous goods rate, and admins are told if the customer said otherwise.',
      missing: 'Open a parcel first: find one above, or pick one from the list.',
    },
    {
      id: 'week',
      page: PAGES.warehouse,
      target: 'warehouse-ship-week',
      title: 'Book the flight week',
      body: 'Flies is the week the parcel goes out of Guangzhou.',
      missing: 'Open a parcel first: find one above, or pick one from the list.',
    },
    {
      id: 'process',
      page: PAGES.warehouse,
      target: 'warehouse-process',
      title: 'Mark it processed',
      body: 'Press Mark processed yourself once the photo and weight are in: Helm prices it and it moves to Received.',
      missing: 'Open a parcel first: find one above, or pick one from the list.',
    },
    {
      id: 'views',
      page: PAGES.warehouse,
      target: 'warehouse-views',
      title: 'Keep track',
      body: 'Expected: announced by customers. Received: processed here. Unclaimed: nobody owns it yet; open it and enter the customer code from the label to claim it.',
    },
  ],
}

export const TOURS: readonly TourDefinition[] = [
  navigation,
  createInquiry,
  quote,
  consignment,
  receiveParcel,
]
